import { useEffect, useRef } from "react";
import { ConnectorInstanceStatus, HealthStatus } from "@axio/graphql/generated/graphql";
import {
  type CatalogConnector,
  type EnabledConnectorInstance,
  hasPersonalUserOauth,
} from "../../components/adminDashboard/Integrations/catalog.utils";
import { useToast } from "../../contexts/Toast/ToastProvider";

const STAGGER_MS = 3000;
const POLL_INTERVAL_MS = 60000;

type StatusKey = "failed" | "active" | "inactive";

function deriveStatusKey(instance: EnabledConnectorInstance, connector?: CatalogConnector): StatusKey {
  const instanceStatus = instance.status;
  const healthStatus = instance.healthStatus?.status;
  const usesPersonalApprovalFlow = connector ? hasPersonalUserOauth(connector) : false;
  const isUnhealthy =
    !usesPersonalApprovalFlow && (healthStatus === HealthStatus.Failed || healthStatus === HealthStatus.Degraded);

  if (instanceStatus === ConnectorInstanceStatus.Error || isUnhealthy) return "failed";
  if (instanceStatus === ConnectorInstanceStatus.Active) return "active";
  return "inactive";
}

interface UseConnectorStatusToastsParams {
  enabledConnectors: EnabledConnectorInstance[];
  connectorCatalog: CatalogConnector[];
  onSelectConnector: (connectorId: string) => void;
}

export function useConnectorStatusToasts({
  enabledConnectors,
  connectorCatalog,
  onSelectConnector,
}: UseConnectorStatusToastsParams) {
  const { showToast } = useToast();
  const enabledConnectorsRef = useRef(enabledConnectors);
  const connectorCatalogRef = useRef(connectorCatalog);
  const hasRunInitial = useRef(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const lastNotifiedStatusRef = useRef(new Map<string, StatusKey>());

  useEffect(() => {
    enabledConnectorsRef.current = enabledConnectors;
    connectorCatalogRef.current = connectorCatalog;
  }, [enabledConnectors, connectorCatalog]);

  const showToastForStatus = (statusKey: StatusKey, label: string, connectorId: string) => {
    switch (statusKey) {
      case "failed":
        showToast({
          message: `${label} connection has failed.`,
          type: "error",
          visibleDuration: 10000,
          actions: [{ actionText: "View Details", actionCallback: () => onSelectConnector(connectorId) }],
        });
        break;
      case "inactive":
        showToast({
          message: `${label} has been deactivated.`,
          type: "info",
          visibleDuration: 10000,
          actions: [{ actionText: "Reconnect", actionCallback: () => onSelectConnector(connectorId) }],
        });
        break;
    }
  };

  const showNonActiveToasts = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    const connectors = enabledConnectorsRef.current;
    const catalog = connectorCatalogRef.current;
    if (!connectors.length || !catalog.length) return;

    let toastIndex = 0;

    for (const instance of connectors) {
      const connector = catalog.find((c) => c.connectorType === instance.connectorType);
      const label = connector?.name ?? instance.connectorType;
      const statusKey = deriveStatusKey(instance, connector);
      const notificationKey = instance.id;

      if (statusKey === "active") {
        lastNotifiedStatusRef.current.delete(notificationKey);
        continue;
      }

      if (lastNotifiedStatusRef.current.get(notificationKey) === statusKey) {
        continue;
      }

      lastNotifiedStatusRef.current.set(notificationKey, statusKey);

      const delay = toastIndex * STAGGER_MS;
      toastIndex++;
      const id = setTimeout(() => {
        showToastForStatus(statusKey, label, connector?.id ?? "");
      }, delay);
      timeoutsRef.current.push(id);
    }
  };

  useEffect(() => {
    if (!enabledConnectors.length || !connectorCatalog.length) return;
    if (hasRunInitial.current) return;

    hasRunInitial.current = true;
    showNonActiveToasts();

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [enabledConnectors, connectorCatalog]);

  useEffect(() => {
    const interval = setInterval(showNonActiveToasts, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  return () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };
}
